import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Header from '@components/common/Header/Header';

import { initialSorting } from '@constants/tableSorting';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import FilterPanel from './components/FilterPanel/FilterPanel';
import Footer from './components/Footer/Footer';
import TableEngagementActionItems from './components/TableEngagementActionItems/TableEngagementActionItems';
import { isQueryEnabled } from './helpers/isQueryEnabled';
import { onChangeSorting } from './helpers/onChangeSorting';
import type { IEngagementStatsActionItem } from './model/engagementStatsActionList';
import { useEngagementListInfiniteQuery } from './services/useEngagementListInfiniteQuery';
import {
  selectEngagementFilterCartRangeValue,
  selectEngagementFilterSearchValue,
  selectEngagementFilterSortingValue,
  selectEngagementFilterState,
  useEngagementFilterActions,
} from './store/useEngagementFilterStore';
import style from './style.module.scss';

const SiteEngagement = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();

  const { setEngagementFilterSortingValue } = useEngagementFilterActions();

  const locationSelectValue = selectAppLocationSelectValue();
  const searchValue = selectEngagementFilterSearchValue();
  const filter = selectEngagementFilterState();
  const cartRange = selectEngagementFilterCartRangeValue();
  const sorting = selectEngagementFilterSortingValue();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const [debouncedCartRangeValueValue] = useDebouncedValue(cartRange, 500);

  const {
    data: engagementListData,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    isRefetching,
  } = useEngagementListInfiniteQuery({
    filter: filter,
    cartRange: debouncedCartRangeValueValue,
    search: debouncedValue,
    sorting,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    enabled: isQueryEnabled(filter),
  });

  const { engagementList, total } = useMemo((): {
    total: number;
    engagementList: IEngagementStatsActionItem[];
  } => {
    if (!engagementListData) {
      return {
        engagementList: [],
        total: 0,
      };
    }

    return {
      engagementList: engagementListData?.pages?.flatMap((page) => page.actionItems) ?? [],
      total: engagementListData?.pages[engagementListData.pages.length - 1]?.total,
    };
  }, [engagementListData]);

  const engagementActionItemsTableContainerRef = useRef(null);

  useEffect(() => {
    trackPageView({});
  }, []);

  return (
    <div className={style.siteEngagementWrapper}>
      <Header
        title={t('Site Engagement AI Report')}
        showLogo={true}
        showFacilitySelect={true}
        isSecondaryPage
      />
      <div className={style.siteEngagementBody}>
        <FilterPanel />
        <TableEngagementActionItems
          data={engagementList}
          sorting={sorting}
          isFetching={isFetching}
          isLoading={isLoading}
          isRefetching={isRefetching}
          hasNextPage={!!hasNextPage}
          onChangeSorting={(key) =>
            onChangeSorting(key, sorting, initialSorting, setEngagementFilterSortingValue)
          }
          fetchNextPage={fetchNextPage}
          engagementActionItemsTableContainerRef={engagementActionItemsTableContainerRef}
        />
      </div>
      <Footer
        items={engagementList.length}
        total={total}
        sorting={sorting}
        engagementActionItemsTableContainerRef={engagementActionItemsTableContainerRef}
      />
    </div>
  );
};

export default SiteEngagement;
